import { useCallback, useEffect, useRef, useState } from "react";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";

import { fetchWithAuth, type AccessTokenGetter } from "@/lib/utils";
import type { Session } from "@/lib/session";
import i18n from "@/lib/i18n";

const PAGE_SIZE = 50;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isSessionListItem(value: unknown): value is Session {
  return (
    isRecord(value)
    && typeof value.id === "string"
  );
}

export interface UseAgentSessionsReturn {
  sessions: Session[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Independent, server-filtered pagination for an agent hub. This deliberately
 * does not reuse the global session list: a hub needs its complete exact-agent
 * history, not whichever pages happen to be loaded in the sidebar.
 */
export function useAgentSessions(
  agentId: string | null | undefined,
  getToken: AccessTokenGetter,
): UseAgentSessionsReturn {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const nextCursorRef = useRef<string | null>(null);
  const hasMoreRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const currentAgentIdRef = useRef<string | null>(agentId ?? null);
  const requestGenerationRef = useRef(0);

  const fetchPage = useCallback(async (
    targetAgentId: string,
    before: string | null,
    append: boolean,
  ): Promise<void> => {
    const request = requestGenerationRef.current + 1;
    requestGenerationRef.current = request;
    if (append) {
      loadingMoreRef.current = true;
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const params = new URLSearchParams();
      params.set("limit", String(PAGE_SIZE));
      params.set("agent_id", targetAgentId);
      if (before) params.set("before", before);
      const response = await fetchWithAuth(`/api/sessions?${params.toString()}`, { headers: {} }, getToken);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const payload: unknown = await response.json();
      const data = isRecord(payload) ? payload : {};
      if (
        request !== requestGenerationRef.current
        || currentAgentIdRef.current !== targetAgentId
      ) return;

      const nextSessions = Array.isArray(data.sessions)
        ? data.sessions.filter(isSessionListItem)
        : [];
      setSessions((current) => {
        const base = append ? current : [];
        const seen = new Set(base.map((session) => session.id));
        return [...base, ...nextSessions.filter((session) => !seen.has(session.id))];
      });
      const nextHasMore = Boolean(data.has_more);
      hasMoreRef.current = nextHasMore;
      nextCursorRef.current = typeof data.next_cursor === "string" ? data.next_cursor : null;
      setHasMore(nextHasMore);
    } catch (err) {
      if (
        request !== requestGenerationRef.current
        || currentAgentIdRef.current !== targetAgentId
      ) return;
      setError(err instanceof Error ? err.message : i18n.t("agent.conversationsLoadFailed"));
      if (!append) {
        nextCursorRef.current = null;
        hasMoreRef.current = false;
        setHasMore(false);
      }
    } finally {
      if (request === requestGenerationRef.current) {
        if (append) {
          loadingMoreRef.current = false;
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    }
  }, [getToken]);

  const refresh = useCallback(async () => {
    const targetAgentId = currentAgentIdRef.current;
    if (!targetAgentId) {
      requestGenerationRef.current += 1;
      nextCursorRef.current = null;
      hasMoreRef.current = false;
      loadingMoreRef.current = false;
      setSessions([]);
      setLoading(false);
      setLoadingMore(false);
      setHasMore(false);
      setError(null);
      return;
    }
    await fetchPage(targetAgentId, null, false);
  }, [fetchPage]);

  const loadMore = useCallback(async () => {
    const targetAgentId = currentAgentIdRef.current;
    if (
      !targetAgentId
      || loadingMoreRef.current
      || !hasMoreRef.current
      || !nextCursorRef.current
    ) return;
    await fetchPage(targetAgentId, nextCursorRef.current, true);
  }, [fetchPage]);

  useRefreshOnReconnect(refresh, Boolean(agentId));

  useEffect(() => {
    const normalized = agentId ?? null;
    currentAgentIdRef.current = normalized;
    // Cancel/ignore all older responses immediately, then replace every
    // pagination marker before issuing the new exact-agent request.
    requestGenerationRef.current += 1;
    nextCursorRef.current = null;
    hasMoreRef.current = false;
    loadingMoreRef.current = false;
    setSessions([]);
    setError(null);
    setHasMore(false);
    setLoadingMore(false);

    if (!normalized) {
      setLoading(false);
      return;
    }
    void fetchPage(normalized, null, false);
  }, [agentId, fetchPage]);

  return { sessions, loading, error, hasMore, loadingMore, loadMore, refresh };
}
